/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, ElementRef, EventEmitter, Input, OnInit, ViewChild} from '@angular/core';
import {NgClass} from '@angular/common';
import {NlrPermissionsDisabledPipe} from '../../../pipe/nlr-permissions-disabled-pipe';
import {NlrFormFieldHasErrorPipe} from '../../../pipe/nlr-form-field-has-error.pipe';
import {ControlContainer, FormsModule, NgForm} from '@angular/forms';
import {NlrTypeheadDirective} from '../../../directive/nlr-typehead.directive';
import {NlrPatternDirective} from '../../../directive/nlr-pattern.directive';
import {NlrUppercaseDirective} from '../../../directive/nlr-uppercase.directive';
import {ModalComponent} from '../modal-service.service';
import {LookupService} from '../../../service/lookup.service';
import {PlanModelService} from '../../../service/plan-model.service';
import {SpatialUtilService} from '../../../../design/services/spatial-util.service';
import {PATTERNS} from '../../../constants';
import {Lga} from '../../../models/lga.model';
import {TraitsService} from '../../../service/traits.service';
import {TenureService} from '../../../../plan-information/tenure/tenure.service';
import {ValidationService} from '../../../service/validation.service';
import {Observable, OperatorFunction} from 'rxjs';
import {debounceTime, distinctUntilChanged, map} from 'rxjs/operators';
import {forkJoinDefaultIfEmpty} from '../../../observable.utils';
import {NlrFocusDirective} from '../../../directive/nlr-focus.directive';
import {NgSelectComponent, NgSelectModule} from '@ng-select/ng-select';
import {ConfirmationModalService} from '../confirmation-modal/confirmation-modal.service';

/**
 * Options offered for the `{Recital}` endorsement placeholder - how the interest / notification was created (WADPL-1120). Named so that the options
 * requiring a document number are tied to the options offered, rather than repeating the literals in both lists.
 *
 * The leading "SEE" is deliberately absent: 78 descriptions already end with `SEE` immediately before `{Recital}`, so carrying it in the option too
 * would read "SEE SEE THIS PLAN". It is supplied by {@link ManualFormerTenureInterestNotificationModalComponent.resolvedEndorsementText} for the
 * descriptions that do not bake it in.
 */
const RECITAL = {
    THIS_PLAN: 'THIS PLAN',
    DOCUMENT: 'DOC.',
    THIS_PLAN_AND_DOCUMENT: 'THIS PLAN AND DOC.'
};

/** The placeholder token the `RECITAL` options belong to, and the only one "SEE" is ever supplied for. */
const RECITAL_TOKEN = 'Recital';

@Component({
    selector: 'manual-former-tenure-interest-notification-modal',
    standalone: true,
    imports: [
        NlrPermissionsDisabledPipe,
        NlrFormFieldHasErrorPipe,
        NgClass,
        FormsModule,
        NlrTypeheadDirective,
        NlrPatternDirective,
        NlrUppercaseDirective,
        NlrFocusDirective,
        NgSelectModule
    ],
    templateUrl: './manual-former-tenure-interest-notification-modal.component.html',
    styleUrls: ['../modal.scss', './manual-former-tenure-interest-notification-modal.component.scss'],
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class ManualFormerTenureInterestNotificationModalComponent implements ModalComponent, OnInit {
    readonly SURVEY_PATTERN = PATTERNS.SURVEY_PATTERN;
    readonly ALPHANUMERIC_PATTERN = PATTERNS.ALPHANUMERIC_PATTERN;
    readonly LOT_PATTERN = PATTERNS.LOT_PATTERN;
    readonly DOCUMENT_PATTERN = PATTERNS.DOCUMENT_PATTERN;

    /**
     * User friendly labels for the endorsement template placeholders. The raw `{Token}` name is never shown to the user - it is only ever used as the
     * key into `endorsementPlaceholders` and within `endorsementTemplate`.
     */
    private readonly PLACEHOLDER_LABELS: {[token: string]: string} = {
        ProprietorName: 'Named beneficiary',
        Recital: 'Created on',
        LeaseInformation: 'Lease information'
    };

    /** Placeholders the user never sees or supplies a value for; they resolve to nothing in the endorsement text. */
    private readonly HIDDEN_PLACEHOLDERS: string[] = ['RecitalPeriod', 'AffectedLots', 'LegalStanding'];

    /**
     * Placeholders offered as a suggestion list. The user may still type a value that is not listed.
     *
     * `ProprietorName` starts empty and is filled in by {@link loadNamedBeneficiaries}, since the local governments are fetched rather than known up
     * front. A `ProprietorName` field shown before the fetch returns is free text only, which is why the list must never be replaced with a new array
     * - the template holds the reference `placeholderOptions` handed it.
     */
    private readonly PLACEHOLDER_OPTIONS: {[token: string]: string[]} = {
        ProprietorName: [],
        Recital: [RECITAL.THIS_PLAN, RECITAL.DOCUMENT, RECITAL.THIS_PLAN_AND_DOCUMENT]
    };

    /** Shared empty list, so a placeholder with no suggestions returns a stable reference to the template. */
    private readonly NO_OPTIONS: string[] = [];

    /** `Recital` options that name a document, and so require the user to say which one. */
    private readonly RECITAL_OPTIONS_NEEDING_DOCUMENT: string[] = [RECITAL.DOCUMENT, RECITAL.THIS_PLAN_AND_DOCUMENT];

    /** Placeholders whose value is optional; every other placeholder must be supplied before the notification can be saved. */
    private readonly OPTIONAL_PLACEHOLDERS: string[] = ['LeaseInformation'];

    /** Only ever used with `match` / `replace`, both of which reset `lastIndex`. */
    private readonly PLACEHOLDER_PATTERN = /\{([A-Za-z]+)\}/g;

    @Input() formerTenures: any[] = [];
    @Input() existingNotification?: any;

    modalClosed: EventEmitter<undefined | string> = new EventEmitter();
    @ViewChild('manualFormerTenureModalForm') form: NgForm;
    @ViewChild('recitalDocumentField') recitalDocumentField?: ElementRef<HTMLInputElement>;

    burdenAffectedLotsDirty = {};

    selectedFormerTenure: any = null;
    newEndorsementType: any = null;
    newEndorsementName: string = '';
    formerTenureInterestNotification: any = {subjects: []};

    notificationTreatmentActionLookups: any[] = [];
    interestSubjectTypeLookups: any[] = [];
    endorsementLookups: any[] = [];

    showSubjectForAction = false;
    currentDocumentLabel = '';
    subjectsInvalid = false;
    fieldErrorMessages = new Map<string, string>();

    constructor(
        private planModelService: PlanModelService,
        private lookupService: LookupService,
        private tenureService: TenureService,
        private validationService: ValidationService,
        private spatialUtilService: SpatialUtilService,
        private traitsService: TraitsService,
        private confirmationModalService: ConfirmationModalService
    ) {}

    ngOnInit(): void {
        this.notificationTreatmentActionLookups = this.lookupService.getLookups('notificationTreatmentAction');
        this.interestSubjectTypeLookups = this.lookupService.getLookups('interestSubjectType');
        this.endorsementLookups = this.lookupService.getLookups('formerTenureNotification');
        this.loadNamedBeneficiaries();

        if (this.existingNotification) {
            this.formerTenureInterestNotification = structuredClone(this.existingNotification);
            this.newEndorsementType = this.endorsementLookups.find((l: any) => l.notificationCode === this.existingNotification.notificationCode);
            this.newEndorsementName = this.endorsementText(this.formerTenureInterestNotification.endorsementTemplate);
            this.formerTenureInterestNotification.endorsementPlaceholders ??= {};
            this.splitRecitalValue(this.formerTenureInterestNotification.endorsementPlaceholders[RECITAL_TOKEN]);
            this.selectedFormerTenure = this.formerTenures.find((ftn: any) => ftn.identifier === this.existingNotification.identifier) ?? null;
            this.showSubjects(this.existingNotification.treatmentAction?.id);
        }

        if (this.formerTenures.length === 1) {
            this.selectedFormerTenure = this.formerTenures[0];
        }
    }

    /**
     * Builds the `{ProprietorName}` suggestion list (WADPL-1120) from the two sources the client's list is drawn from: the utilities, authorities and
     * corporations already held as the `interestBenefit` lookup, and WA local governments fetched from GNA. Both arrive upper case, matching the
     * endorsement text they are substituted into.
     *
     * The lookup names are trimmed because at least one CSV row carries a trailing space. Local governments are appended when they arrive rather than
     * awaited, so the field is usable - as free text, or with the utilities alone - if GNA is unavailable. The named beneficiary is a suggestion,
     * not a closed set, so an incomplete list degrades the field rather than blocking it.
     */
    private loadNamedBeneficiaries = () => {
        const namedBeneficiaries = this.PLACEHOLDER_OPTIONS['ProprietorName'];
        const utilities = this.lookupService.getLookups<{name: string}>('interestBenefit').map(b => b.name?.trim());

        namedBeneficiaries.splice(0, namedBeneficiaries.length, ...this.sortedDistinct(utilities));

        this.lookupService.retrieveLookup<Lga[]>('/locationname/findAllLocalGovernmentAuthorities').subscribe({
            next: lgas => {
                const names = [...namedBeneficiaries, ...lgas.map(lga => lga.localGovernmentAuthority?.trim())];
                namedBeneficiaries.splice(0, namedBeneficiaries.length, ...this.sortedDistinct(names));
            },
            error: error => console.error('Named beneficiary LGA load failed ' + error.message)
        });
    };

    /** Alphabetical, with blanks and duplicates dropped - the two beneficiary sources overlap in principle and are separately maintained. */
    private sortedDistinct = (names: string[]): string[] => [...new Set(names.filter(name => !!name))].sort((a, b) => a.localeCompare(b));

    readOnly = () => {
        return this.planModelService.readonly;
    };

    dismissModal = (form: any) => {
        if (form.pristine) {
            this.modalClosed.emit('Closed');
            return;
        }
        this.confirmationModalService
            .showModal({
                title: 'Warning',
                confirmationText: `You are about to lose all changes. Are you sure you wish to continue?`
            })
            .subscribe(action => {
                if (action === 'yes') {
                    this.modalClosed.emit('Closed');
                }
            });
    };

    /**
     * User facing label for a placeholder token. Tokens without a configured label fall back to their name split on word boundaries, so a new token
     * added to the lookup still reads sensibly.
     */
    placeholderLabel = (token: string): string => {
        if (this.PLACEHOLDER_LABELS[token]) {
            return this.PLACEHOLDER_LABELS[token];
        }
        const words = token.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
        return words.charAt(0).toUpperCase() + words.slice(1);
    };

    /** The endorsement description as the user sees it: every `{Token}` replaced by its label, omitted tokens removed entirely. */
    private endorsementText = (description: string): string =>
        (description ?? '')
            .replace(this.PLACEHOLDER_PATTERN, (_: string, name: string) =>
                this.HIDDEN_PLACEHOLDERS.includes(name) ? '' : ` {${this.placeholderLabel(name)}}`
            )
            .replace(/\s+/g, ' ')
            .trim();

    private endorsementDisplay = (l: any): string => `${l.notificationCode} ${this.endorsementText(l.description)}`.trim();

    endorsementTypeahead: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            map(term =>
                this.endorsementLookups
                    .filter(
                        (l: any) =>
                            l.active && !l.legacy && l.description != null && this.endorsementDisplay(l).toLowerCase().includes(term.toLowerCase())
                    )
                    .sort((a: any, b: any) => String(a.notificationCode).localeCompare(String(b.notificationCode)))
                    .map((l: any) => this.endorsementDisplay(l))
            )
        );

    endorsementTypeSelected = (event: any) => {
        const selected = this.endorsementLookups.find((l: any) => this.endorsementDisplay(l) === event);
        if (selected) {
            this.newEndorsementType = selected;
            this.newEndorsementName = this.endorsementText(selected.description);
            this.formerTenureInterestNotification.endorsementTemplate = selected.description;
            this.formerTenureInterestNotification.endorsementPlaceholders = {};
            this.resetRecital();
            this.formerTenureInterestNotification.treatmentAction = null;
            this.formerTenureInterestNotification.subjects = [];
            this.showSubjectForAction = false;
            this.currentDocumentLabel = '';
        }
    };

    endorsementModelChange = () => {
        if (!this.newEndorsementName) {
            this.newEndorsementType = null;
            this.formerTenureInterestNotification.endorsementTemplate = null;
            this.formerTenureInterestNotification.endorsementPlaceholders = {};
            this.resetRecital();
            this.formerTenureInterestNotification.treatmentAction = null;
            this.formerTenureInterestNotification.subjects = [];
            this.showSubjectForAction = false;
            this.currentDocumentLabel = '';
        }
    };

    /**
     * The placeholder tokens the user is asked to supply a value for. The token itself is the `endorsementPlaceholders` key and is never rendered -
     * use {@link placeholderLabel} for anything user facing.
     */
    get placeholders(): string[] {
        const matches = (this.formerTenureInterestNotification.endorsementTemplate ?? '').match(this.PLACEHOLDER_PATTERN) ?? [];
        const seen = new Set<string>();
        return matches
            .map((m: string) => m.slice(1, -1))
            .filter((name: string) => !this.HIDDEN_PLACEHOLDERS.includes(name))
            .filter((name: string) => (seen.has(name) ? false : (seen.add(name), true)));
    }

    get resolvedEndorsementText(): string {
        const template = this.formerTenureInterestNotification.endorsementTemplate;
        if (!template) {
            return '';
        }
        const values = this.formerTenureInterestNotification.endorsementPlaceholders ?? {};
        return template
            .replace(this.PLACEHOLDER_PATTERN, (match: string, name: string, offset: number) => {
                const value = values[name]?.trim();
                if (!value) {
                    return '';
                }
                return name === RECITAL_TOKEN && !this.endsWithSee(template.slice(0, offset)) ? ` SEE ${value}` : ` ${value}`;
            })
            .trim();
    }

    /**
     * Whether the description text before `{Recital}` already ends with the word "SEE" - 78 of them do, which is why the options themselves omit it.
     * Matched on the text preceding the token rather than the whole description, since `{Recital}` is not always last. Any placeholder in between is
     * ignored: only `LeaseInformation` is optional, so every other one is filled in before the notification can be saved.
     */
    private endsWithSee = (precedingText: string): boolean => /(^|\s)SEE$/.test(precedingText.trimEnd());

    /** The suggestion list for a placeholder, or an empty array when it is free text only. */
    placeholderOptions = (token: string): string[] => this.PLACEHOLDER_OPTIONS[token] ?? this.NO_OPTIONS;

    /** Endorsement text is upper case throughout, so a value the user types is upper cased to match the listed options. */
    private normalisePlaceholderValue = (value: string): string => (value ?? '').trim().toUpperCase();

    /**
     * What the user has typed into each suggestion field. `ng-select` treats what is typed as a filter over the options rather than as a value, and
     * discards it on close - so it is remembered here and taken as the value when the user leaves the field without picking from the list. It must be
     * captured as it is typed rather than read on the way out, because `ng-select` has already cleared its own copy by then.
     */
    private typedTerms: {[token: string]: string} = {};

    placeholderTermTyped = (token: string, term: string) => {
        this.typedTerms[token] = this.normalisePlaceholderValue(term);
    };

    /** Discards the remembered term, so leaving the field cannot overwrite a value picked from the list, nor one the user abandoned with Escape. */
    forgetPlaceholderTerm = (token: string) => {
        delete this.typedTerms[token];
    };

    /** Takes what the user typed as the placeholder value, so free text needs no confirming step. Nothing typed leaves the existing value alone. */
    commitPlaceholderTerm = (token: string) => {
        const term = this.typedTerms[token];
        this.forgetPlaceholderTerm(token);
        if (term) {
            this.formerTenureInterestNotification.endorsementPlaceholders[token] = term;
        }
    };

    /**
     * Commits what the user typed when they press Enter with nothing highlighted in the list. When something is highlighted `ng-select` selects that
     * instead, so the two never both act on the same keystroke.
     */
    commitPlaceholderTermOnEnter = (token: string, select: NgSelectComponent) => {
        if (!select.itemsList.markedItem) {
            this.commitPlaceholderTerm(token);
            select.close();
        }
    };

    isPlaceholderRequired = (token: string): boolean => !this.OPTIONAL_PLACEHOLDERS.includes(token);

    /**
     * `Recital` is stored as the chosen option followed by the document number, e.g. {@code DOC. K123456}. The option and the document number are
     * edited as two controls, so both are held separately and recombined into the placeholder value.
     */
    recitalOption: string = null;
    recitalDocument: string = '';

    /** Whether the chosen `Recital` option names a document, and so needs the document number field alongside it. */
    get recitalNeedsDocument(): boolean {
        return this.RECITAL_OPTIONS_NEEDING_DOCUMENT.includes(this.recitalOption);
    }

    /**
     * Splits a stored `Recital` value back into the option and document number, so an existing notification reopens with both controls populated. The
     * options are matched longest first because {@code THIS PLAN} is a prefix of {@code THIS PLAN AND DOC.}.
     */
    private splitRecitalValue = (value: string) => {
        const stored = (value ?? '').trim();
        const option = [...this.PLACEHOLDER_OPTIONS[RECITAL_TOKEN]]
            .sort((a: string, b: string) => b.length - a.length)
            .find((o: string) => stored === o || stored.startsWith(`${o} `));
        this.recitalOption = option ?? (stored ? stored : null);
        this.recitalDocument = option ? stored.slice(option.length).trim() : '';
    };

    private resetRecital = () => {
        this.recitalOption = null;
        this.recitalDocument = '';
    };

    /** Takes what the user typed as the `Recital` option. Free text is never one of the document options, so no document number is asked for. */
    commitRecitalTerm = () => {
        const term = this.typedTerms[RECITAL_TOKEN];
        this.forgetPlaceholderTerm(RECITAL_TOKEN);
        if (term) {
            this.recitalOption = term;
            this.recitalOptionChanged();
        }
    };

    /** As {@link commitPlaceholderTermOnEnter}, for the `Recital` option. */
    commitRecitalTermOnEnter = (select: NgSelectComponent) => {
        if (!select.itemsList.markedItem) {
            this.commitRecitalTerm();
            select.close();
        }
    };

    /**
     * The user picked a `Recital` option. Clears any document number the new option has no use for, then moves focus to the document field if it does
     * want one - the option is chosen with the mouse or Enter, so the caret would otherwise be left nowhere useful.
     */
    recitalOptionChanged = () => {
        if (!this.recitalNeedsDocument) {
            this.recitalDocument = '';
            this.applyRecitalValue();
            return;
        }
        this.applyRecitalValue();
        this.focusRecitalDocument();
    };

    /** The document number was typed into. Only the stored value needs rebuilding - focus is already where it belongs. */
    recitalDocumentChanged = () => this.applyRecitalValue();

    /**
     * Focuses the document number field. Deferred a tick because the field is created by `@if`, so it is not yet in the DOM on the change that
     * reveals it. Done imperatively rather than with `nlrFocus` because both document-naming options share the one field: switching between
     * `DOC.` and `THIS PLAN AND DOC.` leaves the element in place, and `nlrFocus` acts only when an element is created.
     */
    private focusRecitalDocument = () => {
        setTimeout(() => this.recitalDocumentField?.nativeElement.focus());
    };

    private applyRecitalValue = () => {
        this.formerTenureInterestNotification.endorsementPlaceholders ??= {};
        const document = this.recitalNeedsDocument ? this.recitalDocument?.trim() : '';
        this.formerTenureInterestNotification.endorsementPlaceholders[RECITAL_TOKEN] = [this.recitalOption, document].filter(Boolean).join(' ');
    };

    get allPlaceholdersFilled(): boolean {
        const values = this.formerTenureInterestNotification.endorsementPlaceholders ?? {};
        return this.placeholders
            .filter((name: string) => this.isPlaceholderRequired(name))
            .every((name: string) => {
                if (name === RECITAL_TOKEN) {
                    return !!this.recitalOption && (!this.recitalNeedsDocument || this.DOCUMENT_PATTERN.test(this.recitalDocument?.trim() ?? ''));
                }
                return !!values[name]?.trim();
            });
    }

    filteredNotificationTreatmentActionLookups = () => {
        if (!this.newEndorsementType) {
            return [];
        }
        return this.newEndorsementType.notificationTreatmentActions
            .map((treatmentAction: string) => this.notificationTreatmentActionLookups.find((v: any) => v.id === treatmentAction))
            .filter((ntal: any) => {
                if (!ntal || !ntal.active) {
                    return false;
                }

                const {parentTenureAssociationType} = this.planModelService.getModel().planPurpose;

                if (ntal.parentTenureAssociationType !== parentTenureAssociationType) {
                    return false;
                }

                if (!ntal.planTraitCriteria) {
                    return true;
                }

                return this.traitsService.criteriaMatches(ntal.planTraitCriteria, false);
            });
    };

    treatmentActionUpdated = (treatmentActionIdSelected: string) => {
        this.showSubjects(treatmentActionIdSelected);
        this.formerTenureInterestNotification.treatmentAction = this.filteredNotificationTreatmentActionLookups().find(
            (ta: any) => ta.id === treatmentActionIdSelected
        );
    };

    showSubjects = (treatmentActionSelected: string) => {
        const newSubjects = [];

        this.formerTenureInterestNotification.treatmentAction = this.notificationTreatmentActionLookups.find(
            (v: any) => v.id === treatmentActionSelected
        );

        if (
            this.formerTenureInterestNotification.treatmentAction &&
            this.formerTenureInterestNotification.treatmentAction.interestSubjectTypes &&
            this.formerTenureInterestNotification.treatmentAction.interestSubjectTypes.length
        ) {
            this.showSubjectForAction = true;
            this.currentDocumentLabel = this.formerTenureInterestNotification.treatmentAction.docNumberFieldLabel || '';

            this.formerTenureInterestNotification.treatmentAction.interestSubjectTypes.forEach((interestSubjectType: string) => {
                const istLookup = this.interestSubjectTypeLookups.find((v: any) => v.id === interestSubjectType);
                const subject = this.formerTenureInterestNotification.subjects.find(
                    (s: any) => s.subjectType && s.subjectType.id === interestSubjectType
                );

                if (subject) {
                    newSubjects.push(subject);
                } else {
                    newSubjects.push({subjectType: istLookup, affectedLots: [{startLot: '', endLot: ''}]});
                }
            });
        } else {
            this.resetSubjectFields();
        }

        this.formerTenureInterestNotification.subjects.splice(0, this.formerTenureInterestNotification.subjects.length, ...newSubjects);

        this.configAffectedLotRanges();
    };

    resetSubjectFields = () => {
        this.showSubjectForAction = false;
        delete this.formerTenureInterestNotification.treatmentDocument;
        this.fieldErrorMessages.delete('treatmentDocument');
        this.currentDocumentLabel = '';
    };

    configAffectedLotRanges = () => {
        this.formerTenureInterestNotification.subjects.forEach((subject: any) => {
            subject.affectedLots.splice(
                0,
                subject.affectedLots.length,
                ...this.tenureService
                    .reduceRanges(subject.affectedLots.map((v: any) => ({from: v.startLot, to: v.endLot})))
                    .map((v: any) => ({startLot: v.from, endLot: v.to}))
            );
        });
    };

    isOriginPlanRequired = () => {
        return this.newEndorsementType?.planOriginRequired ?? false;
    };

    originPlanChanged = (form: any) => {
        const currentErrors = form.controls['originPlan']?.errors;
        if (currentErrors) {
            delete currentErrors.validIdentifier;
        }
        form.controls['originPlan']?.setErrors(currentErrors);
        this.fieldErrorMessages.delete('originPlan');
    };

    getFieldErrorMessage = (fieldName: string) => {
        return this.fieldErrorMessages.get(fieldName);
    };

    actionPdfCreationMethodInvalidForPlan = () => {
        if (
            this.formerTenureInterestNotification.treatmentAction &&
            this.formerTenureInterestNotification.treatmentAction.supportedPdfCreationMethods
        ) {
            const planPDFCreationMethod = this.planModelService.getModel().pdfCreationMethod;
            return !this.formerTenureInterestNotification.treatmentAction.supportedPdfCreationMethods.some(
                (spcm: string) => planPDFCreationMethod === spcm
            );
        }
        return undefined;
    };

    unsupportedMessage = () => {
        return this.formerTenureInterestNotification.treatmentAction?.unsupportedMessage;
    };

    isDuplicateSubject = (interestSubject: any, subjectIndex: number) => {
        if (interestSubject.subject) {
            const upperCaseSubject = interestSubject.subject.toUpperCase();

            this.subjectsInvalid =
                this.planModelService
                    .getModel()
                    .formerTenures.some((ft: any) =>
                        (ft.formerTenureNotifications ?? []).some((ftn: any) =>
                            ftn.subjects.some(
                                (subject: any) =>
                                    subject.subject && subject.subject.toUpperCase() === upperCaseSubject && subject.id !== interestSubject.id
                            )
                        )
                    ) ||
                this.formerTenureInterestNotification.subjects.some(
                    (subject: any, index: number) =>
                        subject.subject && subject.subject.toUpperCase() === upperCaseSubject && index !== subjectIndex && subject.subject.length > 0
                );
        } else {
            this.subjectsInvalid = false;
        }

        return this.subjectsInvalid;
    };

    showAffectedLots = (index: number) => {
        const interestsUiConfigLookup = this.lookupService
            .getLookups('interestsUiConfig')
            .find((iucl: any) => iucl.parentTenureAssociationType === this.planModelService.getModel().planPurpose.parentTenureAssociationType);

        if (interestsUiConfigLookup && interestsUiConfigLookup.broughtForwardNotificationsNewTenureFieldLabel) {
            if (
                !this.formerTenureInterestNotification.subjects[index].affectedLots ||
                this.formerTenureInterestNotification.subjects[index].affectedLots.length === 0
            ) {
                this.formerTenureInterestNotification.subjects[index].affectedLots = [];
                this.formerTenureInterestNotification.subjects[index].affectedLots.push({startLot: '', endLot: ''});
            }
            return true;
        }
        return false;
    };

    getLabel = (labelName: string) => {
        const interestsUiConfigLookup = this.lookupService
            .getLookups('interestsUiConfig')
            .find((iucl: any) => iucl.parentTenureAssociationType === this.planModelService.getModel().planPurpose.parentTenureAssociationType);
        return interestsUiConfigLookup ? interestsUiConfigLookup[labelName] : '';
    };

    onChangeAffectedLot = (interestSubject: any, idx: number, newAffectedLot: any) => {
        this.burdenAffectedLotsDirty[idx] = true;
        if (newAffectedLot) {
            const lots = newAffectedLot
                .split('-')
                .map((v: string) => parseInt(v))
                .filter((v: number) => !isNaN(v));
            if (lots.length === 1) {
                Object.assign(interestSubject.affectedLots[idx], {startLot: lots[0], endLot: lots[0]});
                return;
            }
            if (lots.length === 2) {
                const [startLot, endLot] = lots;
                Object.assign(interestSubject.affectedLots[idx], {startLot: startLot, endLot: endLot});
                return;
            }
            Object.assign(interestSubject.affectedLots[idx], newAffectedLot);
        } else {
            interestSubject.affectedLots[idx] = {startLot: '', endLot: ''};
        }
    };

    getBurdenAffectedLotForDisplay = (burdenAffectedLot: any) => {
        if (burdenAffectedLot) {
            if (burdenAffectedLot.startLot && burdenAffectedLot.endLot && burdenAffectedLot.startLot !== burdenAffectedLot.endLot) {
                return `${burdenAffectedLot.startLot}-${burdenAffectedLot.endLot}`;
            }
            if (burdenAffectedLot.startLot) {
                return `${burdenAffectedLot.startLot}`;
            }
        }
        return '';
    };

    addLotRecord = (interestSubject: any) => {
        interestSubject.affectedLots.push({});
    };

    removeLotRecord = (interestSubject: any, burdenAffectedLot: any) => {
        interestSubject.affectedLots.splice(
            0,
            interestSubject.affectedLots.length,
            ...interestSubject.affectedLots.filter((affectedLot: any) => affectedLot !== burdenAffectedLot)
        );
    };

    treatmentDocumentChanged = (form: any) => {
        const currentErrors = form.controls['treatmentDocument']?.errors;
        if (currentErrors) {
            delete currentErrors.validIdentifier;
        }
        this.fieldErrorMessages.delete('treatmentDocument');
    };

    burdenActionHasError = () => {
        if (!this.form) {
            return false;
        }
        let error = false;
        Object.keys(this.form.controls).forEach(key => {
            if (key.startsWith('burdenAction')) {
                if (this.form.controls[key].invalid && this.form.controls[key].dirty) {
                    error = true;
                }
            }
        });
        return error;
    };

    burdenAffectedLotHasError = (ctrlIdx: number) => {
        if (!this.form || !this.form.controls[`burdenAffectedLot${ctrlIdx}`]) {
            return false;
        }
        return this.form.controls[`burdenAffectedLot${ctrlIdx}`].invalid && this.burdenAffectedLotsDirty[ctrlIdx];
    };

    save = () => {
        if (this.form.invalid || this.subjectsInvalid || !this.allPlaceholdersFilled) {
            return;
        }

        const observables: Observable<unknown>[] = [];

        if (this.formerTenureInterestNotification.originPlan) {
            const surveyIdObs = this.validationService.validateSurveyIdentifier(this.formerTenureInterestNotification.originPlan);
            surveyIdObs.subscribe((valid: boolean) => {
                if (valid) {
                    this.form.controls['originPlan']?.setErrors(null);
                    this.fieldErrorMessages.delete('originPlan');
                } else {
                    this.form.controls['originPlan']?.setErrors({validIdentifier: !valid});
                    this.fieldErrorMessages.set('originPlan', 'Plan number is not a valid number');
                }
            });
            observables.push(surveyIdObs);
        }

        if (this.formerTenureInterestNotification.treatmentDocument) {
            const docIdObs = this.validationService.validateDocumentIdentifier(this.formerTenureInterestNotification.treatmentDocument);
            docIdObs.subscribe((valid: boolean) => {
                if (valid) {
                    this.form.controls['treatmentDocument']?.setErrors(null);
                    this.fieldErrorMessages.delete('treatmentDocument');
                } else {
                    this.form.controls['treatmentDocument']?.setErrors({validIdentifier: !valid});
                    this.fieldErrorMessages.set('treatmentDocument', 'Document number is not a valid number');
                }
            });
            observables.push(docIdObs);
        }

        forkJoinDefaultIfEmpty(observables).subscribe(() => {
            if (this.fieldErrorMessages.size === 0) {
                this.saveAndUpdate();
            }
        });
    };

    saveAndUpdate = () => {
        this.sortSubjectLots();
        this.reduceAffectedLotRanges();

        this.formerTenureInterestNotification.digital = true;
        this.formerTenureInterestNotification.portalCreated = true;
        this.formerTenureInterestNotification.identifier = this.selectedFormerTenure.identifier;
        this.formerTenureInterestNotification.notificationCode = this.newEndorsementType.notificationCode;
        this.formerTenureInterestNotification.notificationCodeOverride = null;
        this.formerTenureInterestNotification.endorsement = this.resolvedEndorsementText;
        this.formerTenureInterestNotification.actionLookup = this.newEndorsementType;
        this.formerTenureInterestNotification.formerTenureId = this.selectedFormerTenure.id;
        this.formerTenureInterestNotification.uid = this.formerTenureInterestNotification.uid ?? this.planModelService.createGuid();

        this.spatialUtilService.save().subscribe(() => {
            this.modalClosed.emit(this.formerTenureInterestNotification);
        });
    };

    sortSubjectLots = () => {
        for (const subject of this.formerTenureInterestNotification.subjects) {
            for (const affectedLots of subject.affectedLots) {
                if (affectedLots.startLot !== affectedLots.endLot) {
                    const startLot = affectedLots.startLot;
                    const endLot = affectedLots.endLot;
                    affectedLots.startLot = startLot > endLot ? endLot : startLot;
                    affectedLots.endLot = startLot > endLot ? startLot : endLot;
                }
            }
        }
    };

    reduceAffectedLotRanges = () => {
        this.formerTenureInterestNotification.subjects.forEach((subject: any) => {
            subject.affectedLots.splice(
                0,
                subject.affectedLots.length,
                ...this.tenureService
                    .reduceRanges(subject.affectedLots.map((v: any) => ({from: v.startLot, to: v.endLot})))
                    .map((v: any) => ({startLot: v.from, endLot: v.to}))
            );

            subject.affectedLots = subject.affectedLots.filter((affectedLot: any) => affectedLot.startLot);
        });
    };
}
